import React from 'react'
import { InferType } from 'yup'
import { captureException } from '@sentry/react'
import { useMutation } from '@tanstack/react-query'
import { useAppTranslation } from '../../../../../hooks/useAppTranslation'
import { useCurrentSociety } from '../../../../../hooks/useCurrentSociety'
import { useToastNotifications } from '../../../../../hooks/useToastNotification'
import { ToastType } from '../../../../common/Toast/toast-type'
import { societyFAQSectionCreateSchema } from '../../../../../forms/schemas/society_faq_section_create'
import { Modal } from '../../../../common/Modal'
import { ModalHeader } from '../../../../common/Modal/ModalHeader'
import { ModalBody } from '../../../../common/Modal/ModalBody'
import { CreateUpdateQuestionsAndAnswersSectionForm } from '../CreateUpdateQuestionsAndAnswersSectionForm'
import { createFaq, patchFaqSection } from '../../../../../api/faqs'

interface CreateUpdateQuestionsAndAnswersSectionModalProps {
  show: boolean
  close: () => void
  children?: React.ReactNode
  sectionId?: string
  /** Ids of the questions in the section, renamed along with it */
  questionIds?: string[]
}

export const CreateUpdateQuestionsAndAnswersSectionModal = ({
  sectionId,
  questionIds = [],
  children,
  show,
  close,
}: CreateUpdateQuestionsAndAnswersSectionModalProps): React.ReactNode => {
  const { translate } = useAppTranslation()
  const { society, isLoading: isLoadingSociety } = useCurrentSociety()
  const { setToastNotification } = useToastNotifications()

  const updateMode = !!sectionId

  const showErrorMessage = (): void => {
    const message = updateMode
      ? 'createUpdateQuestionsAndAnswers.flashMessage.updateSectionFailure'
      : 'createUpdateQuestionsAndAnswers.flashMessage.createSectionFailure'
    setToastNotification(ToastType.DANGER, translate(message))
  }

  const showSuccessMessage = (): void => {
    const message = updateMode
      ? 'createUpdateQuestionsAndAnswers.flashMessage.updateSectionSuccess'
      : 'createUpdateQuestionsAndAnswers.flashMessage.createSectionSuccess'
    setToastNotification(ToastType.SUCCESS, translate(message))
  }

  const { mutate: mutatePatchFaq, isPending: isPatchingFaq } = useMutation({
    mutationFn: patchFaqSection,
    onSuccess: () => {
      close()
      showSuccessMessage()
    },
    onError: () => {
      showErrorMessage()
    },
  })

  const { mutate: mutateCreateFaq, isPending: isCreatingFaq } = useMutation({
    mutationFn: createFaq,
    onSuccess: () => {
      close()
      showSuccessMessage()
    },
    onError: () => {
      showErrorMessage()
    },
  })
  const onSubmit = async (
    data: InferType<typeof societyFAQSectionCreateSchema>
  ): Promise<void> => {
    if (updateMode) {
      mutatePatchFaq({ id: sectionId, body: data, questionIds })
    } else {
      society &&
        mutateCreateFaq({
          ...data,
          societyId: society._id,
        })
    }
  }

  const onError = (error: unknown): void => {
    captureException(error)
    showErrorMessage()
  }

  const loading = isLoadingSociety || isPatchingFaq || isCreatingFaq

  return (
    <>
      {children}
      <Modal show={show}>
        {{
          header: (
            <ModalHeader onClose={close}>
              {translate(
                updateMode
                  ? 'createUpdateQuestionsAndAnswers.editSectionTitle'
                  : 'createUpdateQuestionsAndAnswers.addNewSectionTitle'
              )}
            </ModalHeader>
          ),
          body: (
            <ModalBody>
              <CreateUpdateQuestionsAndAnswersSectionForm
                onError={onError}
                onSubmit={onSubmit}
                onClose={close}
                loading={loading}
                sectionId={sectionId}
              />
            </ModalBody>
          ),
        }}
      </Modal>
    </>
  )
}
